import React from 'react';
import { reaction } from 'mobx';
import { difference, memoize, merge, pick } from 'lodash';
import classNames from 'classnames';
import { getASNValues } from 'app/util/queryResults';
import { Box, EmptyState, Flex, showInfoToast, Spinner } from 'core/components';
import { showErrorToast } from 'core/components/toast';
import makeCancelable, { CanceledError } from 'core/util/cancelablePromise';
import { OCI } from 'app/views/hybrid/maps/cloudDimensionConstants';
import TrafficLinkGenerator from 'app/views/hybrid/maps/components/TrafficLinkGenerator';
import { inject, observer } from 'mobx-react';
import { withTheme } from 'styled-components';
import CloudMapConnector from 'app/views/hybrid/utils/cloud/connector';

import ItemGrid from 'app/views/hybrid/maps/components/ItemGrid';
import {
  getTotalWidth,
  GRID_ROWS_GAP,
  ICON_SIZE,
  INTERNET_WIDTH,
  LOCATION_PADDING,
  ON_PREM_FLEX_GAP
} from 'app/views/hybrid/utils/cloud/constants';
import { ENTITY_TYPES } from '@kentik/ui-shared/hybrid/constants';
import { generateLinksForHighlightedNode, getMapClassname, rewriteFallbackLinks } from 'app/views/hybrid/utils/map';
import CloudIcon from 'app/views/hybrid/maps/components/CloudIcon';
import {
  getCloudSubnetFilterField,
  getInternetDimension,
  getInternetFilterField
} from 'app/views/hybrid/maps/components/popovers/queryOptionsHelper';
import {
  clearAllEntityFocusContexts,
  clearRedundantCloudProviderContext,
  clearGeneralTopologyContext,
  clearCSPSpecificContexts
} from '../cloudMapContextUtils';

import { OciRegion } from './components';
import AbstractMap from '../components/AbstractMap';
import CloudMapBox from '../components/CloudMapBox';
import TopKeys from '../components/TopKeys/TopKeys';
import withPopover from '../components/popovers/withPopover';

const {
  ROUTER,
  REGION,
  SUBNET,
  NAT_GATEWAY,
  VIRTUAL_CIRCUIT,
  SERVICE_GATEWAY,
  INTERNET_GATEWAY,
  IP_SEC_CONNECTION,
  VIRTUAL_CLOUD_NETWORK,
  LOCAL_PEERING_GATEWAY,
  DYNAMIC_ROUTING_GATEWAY,
  DYNAMIC_ROUTING_GATEWAY_ATTACHMENT
} = ENTITY_TYPES.get('oci');

@withPopover
@withTheme
@inject('$hybridMap', '$kaizen')
@observer
export default class OciMap extends AbstractMap {
  constructor(props) {
    super(props);

    Object.assign(this.state, {
      expandedRegionItems: [],
      activeNode: null,
      boxExpanded: {
        internet: true,
        onprem: true
      },
      loading: true
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { sidebarSettings } = this.props;
    const mapSearchChanged = prevProps.sidebarSettings.searchTerm !== sidebarSettings.searchTerm;

    if (mapSearchChanged) {
      this.topologyCollection.filter(sidebarSettings.searchTerm);
    }

    // Add AI context when topology changes and on-premises sections are visible
    if (prevState.loading && !this.state.loading) {
      this.addOnPremContextIfVisible();
    }

    super.componentDidUpdate(prevProps, prevState);
  }

  addOnPremContextIfVisible = () => {
    const { $hybridMap, $kaizen } = this.props;
    const { topology } = $hybridMap.ociCloudMapCollection;

    if (!$kaizen || !topology) {
      return;
    }

    // Remove redundant generic cloud provider context when viewing OCI-specific topology
    this.clearRedundantCloudProviderContext();

    // Add context for on-premises routers if they exist
    const totalRouters = topology.Routers?.length || 0;

    if (totalRouters > 0) {
      // Clean up previous context
      const existingRoutersContext = {
        type: 'topology_overview',
        name: 'On Prem Routers',
        context: 'Previous on-premises routers view context'
      };
      $kaizen.removeChatContext(existingRoutersContext);

      const routersContext = {
        type: 'topology_overview',
        name: 'On Prem Routers',
        entity: 'router',
        context:
          `User is viewing on-premises infrastructure section with ${totalRouters} routers connected to OCI. ` +
          "These represent the user's on-premises network devices.",
        model: {
          routersCount: totalRouters,
          cloudProvider: 'oci'
        },
        suggestions: [
          'Tell me about my on-premises network devices',
          'How are these routers connected to OCI?',
          'Show me traffic from on-premises to OCI',
          'What network connectivity options are available?'
        ]
      };
      $kaizen.addChatContext(routersContext);
    }

    // Add context for on-premises connectivity topology if it exists
    const totalIPSecConnections = topology.IpSecConnections?.length || 0;

    if (totalIPSecConnections > 0) {
      // Clean up previous context
      const existingTopologyContext = {
        type: 'topology_overview',
        name: 'On Prem Topology',
        context: 'Previous on-premises topology view context'
      };
      $kaizen.removeChatContext(existingTopologyContext);

      const topologyContext = {
        type: 'topology_overview',
        name: 'On Prem Topology',
        entity: 'ipSecConnection',
        context: `User is viewing on-premises connectivity topology with ${totalIPSecConnections} IPSec connections to OCI.`,
        model: {
          ipSecConnectionsCount: totalIPSecConnections,
          cloudProvider: 'oci'
        },
        suggestions: [
          'Explain my on-premises connectivity to OCI',
          'What are the IPSec connection details?',
          'Show me the status of my connections',
          'How can I optimize my network connectivity?'
        ]
      };
      $kaizen.addChatContext(topologyContext);
    }
  };

  clearGeneralTopologyContext = () => {
    const { $kaizen } = this.props;
    clearGeneralTopologyContext($kaizen, 'OCI');
  };

  clearAllEntityFocusContexts = () => {
    const { $kaizen } = this.props;
    clearAllEntityFocusContexts($kaizen);
  };

  clearRedundantCloudProviderContext = () => {
    const { $kaizen } = this.props;
    clearRedundantCloudProviderContext($kaizen);
  };

  clearCSPSpecificContexts = () => {
    const { $kaizen } = this.props;
    clearCSPSpecificContexts($kaizen, {
      cloudProvider: 'oci',
      cloudProviderDisplay: 'OCI',
      additionalKeywords: ['vcn']
    });
  };

  componentDidMount() {
    const { $hybridMap } = this.props;
    this.initialSidebarParams = this.getSidebarParamsFromLocation();

    const { compartmentId } = this.initialSidebarParams || {};

    if (compartmentId && !$hybridMap.selectedOciCompartments.includes(compartmentId)) {
      // override the subscription ids in the settings model
      // this will allow for the UI to display the correct number of subscriptions (1) that are selected
      // this will not save the settings and the user will be able to add more subscriptions to the filter
      // also, the subscription panel will offer a way to detect a filtered state and a method for completely clearing it and returning to the last known set of subscriptions in the settings
      $hybridMap.settingsModel.set({
        ociCompartmentIds: [compartmentId]
      });
    }

    this.fetchTopology();

    this.topologyFetcherDisposer = reaction(
      () => $hybridMap.hasOciCompartmentsDiff,
      () => this.fetchTopology(),
      { delay: 0, fireImmediately: false }
    );
  }

  componentWillUnmount() {
    // execute to avoid memory leak
    this.topologyFetcherDisposer();

    // Clear all CSP-specific contexts when leaving the map
    this.clearCSPSpecificContexts();
  }

  shouldLinksRedraw(prevProps, prevState) {
    const { nodeLinksLoading, expandedRegionItems, activeNode, hoveredNode, isLoading } = this.state;

    if (isLoading) {
      return false;
    }

    if (super.shouldLinksRedraw(prevProps, prevState)) {
      return true;
    }

    if (expandedRegionItems.length !== prevState.expandedRegionItems.length) {
      return true;
    }

    if (prevState.activeNode?.value !== activeNode?.value) {
      return true;
    }

    if (prevState.hoveredNode?.value !== hoveredNode?.value) {
      return true;
    }

    if (prevState.nodeLinksLoading !== nodeLinksLoading) {
      return true;
    }

    const expandedRegionItemValues = expandedRegionItems.map((item) => item.value);
    const prevExpandedRegionItemValues = prevState.expandedRegionItems.map((item) => item.value);

    return difference(expandedRegionItemValues, prevExpandedRegionItemValues).length > 0;
  }

  shouldTargetLinkRollUp(type) {
    return ['cloud', 'internet'].includes(type);
  }

  async fetchTopology() {
    const { $hybridMap, sidebarSettings } = this.props;
    this.setState({ loading: true });

    if (this.pendingPromise) {
      this.pendingPromise.cancel();
    }

    this.pendingPromise = makeCancelable(
      $hybridMap.ociCloudMapCollection.fetch({
        force: true,
        query: sidebarSettings.timeRange,
        data: { compartmentIds: $hybridMap.selectedOciCompartments }
      })
    );

    return this.pendingPromise.promise
      .then(() => {
        const { topology } = $hybridMap.ociCloudMapCollection;

        // Add AI context for topology loading
        if (this.props.$kaizen) {
          const vcnCount = Object.keys(topology.Entities[VIRTUAL_CLOUD_NETWORK] || {}).length;
          const subnetCount = Object.keys(topology.Entities[SUBNET] || {}).length;
          const regionCount = topology.Hierarchy.regions?.length || 0;

          // Remove previous topology context before adding the new one
          if (typeof this.props.$kaizen.removeChatContext === 'function') {
            this.props.$kaizen.removeChatContext('Current OCI Topology');
          }

          const topologyContext = {
            type: 'topology_loaded',
            name: 'Current OCI Topology',
            placeholder: 'Ask anything about your OCI cloud topology',
            entity: 'virtualCloudNetwork',
            context:
              `OCI cloud topology loaded for ${$hybridMap.selectedOciCompartments.length} compartments. Contains ${vcnCount} VCNs, ${subnetCount} subnets across ${regionCount} regions. ` +
              `Time range: ${sidebarSettings.timeRange.start} to ${sidebarSettings.timeRange.end}.`,
            model: {
              cloudProvider: 'oci',
              compartmentIds: $hybridMap.selectedOciCompartments,
              vcnCount,
              subnetCount,
              regionCount,
              timeRange: sidebarSettings.timeRange
            },
            suggestions: [
              'What resources do I have across all regions?',
              'Show me the largest VCNs by subnet count',
              'Are there any security issues in my topology?'
            ]
          };
          this.props.$kaizen.addChatContext(topologyContext);
        }

        this.setState({ loading: false });
      })
      .catch((err) => {
        this.setState({ loading: false }, () => {
          if (!(err instanceof CanceledError)) {
            console.error('Error loading topology', err);
            showErrorToast('Error occurred attempting to load topology');
          } else {
            console.warn('Promise was canceled.');
          }
        });
      })
      .finally(() => {
        this.pendingPromise = null;
        this.setState({ loading: false });
      })
      .then(() => {
        this.setInitialState();
      });
  }

  setInitialState() {
    const { $hybridMap } = this.props;
    const { topology } = $hybridMap.ociCloudMapCollection;
    if (!this.hasAppliedInitialSidebarParams) {
      const initialSidebarConfig = this.getInitialSidebarConfig({ topology });

      if (initialSidebarConfig) {
        const { entityType, nodeData, value } = initialSidebarConfig;

        this.hasAppliedInitialSidebarParams = true;
        this.initialSidebarParams = null;

        this.handleShowDetails({ type: entityType, value, nodeData });
        this.scrollNodeIntoView({ type: entityType, value });
      }
    }
  }

  /*
    @borrowed from CloudAwsMap
  */
  makeLinkQuery(overrides) {
    const { cloudProvider, $hybridMap } = this.props;

    const baseQuery = {
      aggregateTypes: ['agg_total_bytes'],
      outsort: 'agg_total_bytes',
      viz_type: 'sankey',
      all_devices: false,
      device_types: [`${cloudProvider}_subnet`],
      show_overlay: false,
      show_total_overlay: false,
      lookback_seconds: 3600,
      topx: 40,
      metric: [],
      filters: {
        connector: 'All',
        filterGroups: []
      }
    };

    return $hybridMap.getQuery(merge(baseQuery, overrides));
  }

  /*
    @override
    @borrowed from CloudAwsMap
  */
  handleNodeLinksUpdate(links) {
    const processedLinks = links.flatMap((l) => l.links);

    if (processedLinks.length === 0) {
      showInfoToast('No connections were found.');
    }

    const convertedLinks = processedLinks.map((link) => this.convertQueryResultLink(link)).flat();

    const nodeLinks = [...this.getNodeLinks(), ...convertedLinks];

    this.setState({
      nodeLinks,
      nodeLinksLoading: false
    });
  }

  /**
   * @override
   * See maps/README.md file for explanation of how nodeLinkQueries need to be structured to interact with
   * TrafficLinkGenerator component.
   */
  getNodeLinkQueries({ selectedNode, activeInternetTabId } = this.state) {
    const { cloudProvider } = this.props;
    const queryDefs = [];

    const { type, value } = selectedNode;

    // query to/from internet
    if (type === 'internet' || type === SUBNET) {
      let additionalFilter;
      const subType = activeInternetTabId;

      if (type === 'internet') {
        additionalFilter = {
          connector: 'All',
          filters: [
            {
              filterField: getInternetFilterField({ subType }),
              operator: '=',
              filterValue: getASNValues(value).asnList
            }
          ]
        };
      }

      if (type === SUBNET) {
        const { vcnId } = selectedNode;

        const vcnEntity = this.topologyCollection.findEntityById(vcnId);

        additionalFilter = {
          connector: 'All',
          filters: [
            {
              filterField: getCloudSubnetFilterField({ cloudProvider }),
              operator: '=',
              filterValue: selectedNode.displayName
            },
            {
              filterField: OCI.BI.VCN,
              operator: '=',
              filterValue: vcnEntity.displayName
            }
          ]
        };
      }

      queryDefs.push({
        type: 'subnetInternet',
        keyTypes: ['internet', SUBNET, SUBNET, 'internet', INTERNET_GATEWAY, NAT_GATEWAY, SERVICE_GATEWAY],
        allowPairs: [
          [SUBNET, 'internet'],
          ['internet', SUBNET]
        ],
        extraInfo: [INTERNET_GATEWAY, NAT_GATEWAY, SERVICE_GATEWAY],
        selectedNode,
        queries: [
          this.makeLinkQuery({
            metric: [
              getInternetDimension({ subType, direction: 'src' }),
              getCloudSubnetFilterField({ cloudProvider, direction: 'src' }),
              getCloudSubnetFilterField({ cloudProvider, direction: 'dst' }),
              getInternetDimension({ subType, direction: 'dst' }),
              OCI.DST[INTERNET_GATEWAY],
              OCI.DST[NAT_GATEWAY],
              OCI.DST[SERVICE_GATEWAY]
            ],
            filters: {
              connector: 'All',
              filterGroups: [
                additionalFilter,
                {
                  connector: 'Any',
                  filters: [
                    { filterField: 'i_trf_profile', operator: '=', filterValue: 'from cloud to outside' },
                    { filterField: 'i_trf_profile', operator: '=', filterValue: 'from outside to cloud' }
                  ]
                }
              ]
            }
          })
        ]
      });
    }

    if (type === SUBNET) {
      const { vcnId } = selectedNode;

      const vcnEntity = this.topologyCollection.findEntityById(vcnId);
      const subnetFilter = [
        {
          filterField: getCloudSubnetFilterField({ cloudProvider }),
          operator: '=',
          filterValue: selectedNode.displayName
        },
        {
          filterField: OCI.BI.VCN,
          operator: '=',
          filterValue: vcnEntity.displayName
        }
      ];

      queryDefs.push({
        type: 'vcnPeering',
        keyTypes: [`src-${SUBNET}`, `src-${LOCAL_PEERING_GATEWAY}`, `dst-${LOCAL_PEERING_GATEWAY}`, `dst-${SUBNET}`],
        allowPairs: [
          [`src-${SUBNET}`, `src-${LOCAL_PEERING_GATEWAY}`],
          [`dst-${LOCAL_PEERING_GATEWAY}`, `dst-${SUBNET}`]
        ],
        selectedNode: { type: selectedNode.type },
        queries: [
          this.makeLinkQuery({
            metric: [OCI.SRC.SUBNET, OCI.SRC[LOCAL_PEERING_GATEWAY], OCI.DST[LOCAL_PEERING_GATEWAY], OCI.DST.SUBNET],
            filters: {
              connector: 'All',
              filterGroups: [
                {
                  connector: 'All',
                  filters: subnetFilter
                }
              ]
            }
          })
        ]
      });

      // subnet to subnet within same vnet
      queryDefs.push({
        type: 'vcnInternal',
        keyTypes: [`src-${SUBNET}`, `dst-${SUBNET}`],
        allowPairs: [
          [`src-${SUBNET}`, `dst-${SUBNET}`],
          [`dst-${SUBNET}`, `src-${SUBNET}`]
        ],
        selectedNode: { type: selectedNode.type },
        queries: [
          this.makeLinkQuery({
            metric: [OCI.SRC.SUBNET, OCI.DST.SUBNET],
            filters: {
              connector: 'All',
              filterGroups: [
                {
                  connector: 'All',
                  filters: [
                    ...subnetFilter,
                    {
                      filterField: OCI.SRC.VCN,
                      operator: '=',
                      filterValue: vcnEntity.displayName
                    },
                    {
                      filterField: OCI.DST.VCN,
                      operator: '=',
                      filterValue: vcnEntity.displayName
                    }
                  ]
                }
              ]
            }
          })
        ]
      });
    }

    // show connections when clicking vcn gateways
    const gatewayConnectorsTypes = [INTERNET_GATEWAY, NAT_GATEWAY, SERVICE_GATEWAY];
    const gatewayConnectorType = gatewayConnectorsTypes.find((connectorType) => connectorType === type);
    if (gatewayConnectorType && OCI.DST[gatewayConnectorType]) {
      const filterField = OCI.DST[gatewayConnectorType];
      const filterValue = selectedNode.displayName;

      queryDefs.push({
        type: 'vcnGatewayInternetTraffic',
        keyTypes: ['src-internet', `src-${SUBNET}`, selectedNode.type, 'dst-internet', `dst-${SUBNET}`],
        allowPairs: [
          ['src-internet', selectedNode.type],
          [selectedNode.type, 'dst-internet'],
          [`src-${SUBNET}`, selectedNode.type],
          [selectedNode.type, `dst-${SUBNET}`]
        ],
        selectedNode: { type: selectedNode.type },
        queries: [
          this.makeLinkQuery({
            metric: ['AS_src', OCI.SRC.SUBNET, filterField, 'AS_dst', OCI.DST.SUBNET],
            filters: {
              connector: 'All',
              filterGroups: [
                {
                  connector: 'All',
                  filters: [{ filterField, operator: '=', filterValue }]
                }
              ]
            }
          })
        ]
      });
    }

    if (type === LOCAL_PEERING_GATEWAY) {
      queryDefs.push({
        type: 'localPeeringGateway',
        keyTypes: [`src-${SUBNET}`, `src-${LOCAL_PEERING_GATEWAY}`, `dst-${LOCAL_PEERING_GATEWAY}`, `dst-${SUBNET}`],
        allowPairs: [
          [`src-${SUBNET}`, `src-${LOCAL_PEERING_GATEWAY}`],
          [`src-${LOCAL_PEERING_GATEWAY}`, `dst-${LOCAL_PEERING_GATEWAY}`],
          [`dst-${LOCAL_PEERING_GATEWAY}`, `dst-${SUBNET}`]
        ],
        selectedNode: { type: selectedNode.type },
        queries: [
          this.makeLinkQuery({
            metric: [OCI.SRC.SUBNET, OCI.SRC[LOCAL_PEERING_GATEWAY], OCI.DST[LOCAL_PEERING_GATEWAY], OCI.DST.SUBNET],
            filters: {
              connector: 'All',
              filterGroups: [
                {
                  connector: 'All',
                  filters: [
                    {
                      filterField: OCI.BI[LOCAL_PEERING_GATEWAY],
                      operator: '=',
                      filterValue: selectedNode.displayName
                    }
                  ]
                }
              ]
            }
          })
        ]
      });
    }

    if (type === DYNAMIC_ROUTING_GATEWAY || type === DYNAMIC_ROUTING_GATEWAY_ATTACHMENT) {
      let filterValue = selectedNode.displayName;

      if (type === DYNAMIC_ROUTING_GATEWAY_ATTACHMENT) {
        const { drgId } = selectedNode;

        const drg = this.topologyCollection.findEntityById(drgId, DYNAMIC_ROUTING_GATEWAY);

        if (drg) {
          filterValue = drg.displayName;
        }
      }

      queryDefs.push({
        type: 'drgTraffic',
        keyTypes: [
          `src-${SUBNET}`,
          `src-${DYNAMIC_ROUTING_GATEWAY}`,
          `src-${VIRTUAL_CLOUD_NETWORK}`,
          `dst-${DYNAMIC_ROUTING_GATEWAY}`,
          `dst-${SUBNET}`,
          `dst-${VIRTUAL_CLOUD_NETWORK}`,
          `dst-${IP_SEC_CONNECTION}`
        ],
        allowPairs: [
          [`src-${SUBNET}`, `src-${DYNAMIC_ROUTING_GATEWAY}`],
          [`src-${DYNAMIC_ROUTING_GATEWAY}`, `dst-${DYNAMIC_ROUTING_GATEWAY}`],
          [`dst-${DYNAMIC_ROUTING_GATEWAY}`, `dst-${SUBNET}`],
          [`dst-${DYNAMIC_ROUTING_GATEWAY}`, `dst-${IP_SEC_CONNECTION}`]
        ],
        extraInfo: [`src-${VIRTUAL_CLOUD_NETWORK}`, `dst-${VIRTUAL_CLOUD_NETWORK}`],
        selectedNode: { type: DYNAMIC_ROUTING_GATEWAY },
        queries: [
          this.makeLinkQuery({
            metric: [
              OCI.SRC[SUBNET],
              OCI.SRC[DYNAMIC_ROUTING_GATEWAY],
              OCI.SRC[VIRTUAL_CLOUD_NETWORK],
              OCI.DST[DYNAMIC_ROUTING_GATEWAY],
              OCI.DST[SUBNET],
              OCI.DST[VIRTUAL_CLOUD_NETWORK],
              OCI.DST[IP_SEC_CONNECTION]
            ],
            filters: {
              connector: 'All',
              filterGroups: [
                {
                  connector: 'All',
                  filters: [
                    {
                      filterField: OCI.BI[DYNAMIC_ROUTING_GATEWAY],
                      operator: '=',
                      filterValue
                    }
                  ]
                }
              ]
            }
          })
        ]
      });
    }

    return queryDefs;
  }

  calculateStaticLinks = memoize(
    () => {
      const { Links = [] } = this.topologyCollection;

      const staticLinks = Links.flatMap((link) =>
        link.map((segment) => {
          const { source, target } = segment;
          const sourceSegment = { type: source.type, value: source.id };
          const targetSegment = { type: target.type, value: target.id };

          if (source.fallbackNodes) {
            sourceSegment.fallbackNodes = source.fallbackNodes;
          }

          if (target.fallbackNodes) {
            targetSegment.fallbackNodes = target.fallbackNodes;
          }

          return {
            isArrowLink: true,
            isForwardPath: true,
            source: sourceSegment,
            target: targetSegment
          };
        })
      );

      return staticLinks.map(this.styleLink);
    },
    () => this.topologyCollection.Links.flat().length
  );

  styleLink = (link) => {
    const { source, target } = link;
    let connectionType = 'curved';

    const squareConnections = [ROUTER, IP_SEC_CONNECTION, VIRTUAL_CIRCUIT, 'internet', 'box'];
    if (squareConnections.includes(source.type) || squareConnections.includes(target.type)) {
      connectionType = 'square';
    }

    if (source.type === LOCAL_PEERING_GATEWAY && target.type === LOCAL_PEERING_GATEWAY) {
      connectionType = 'square';
    }

    return { connectionType, ...link };
  };

  isVcnExpanded(vncId) {
    const { expandedRegionItems } = this.state;

    return expandedRegionItems.some((item) => item.value === vncId);
  }

  /**
   * will convert value as display name that comes from DE to id for lines drawing
   * will remove src-, dst-
   */
  convertQueryResultLink(link) {
    let resultedLink = { ...link };

    ['source', 'target'].forEach((direction) => {
      if (link[direction].type === SUBNET) {
        const subnetName = link[direction].value;
        const virtualCloudNetworkPrefix = direction === 'source' ? 'src' : 'dst';

        const vcnName = link[`${virtualCloudNetworkPrefix}-${VIRTUAL_CLOUD_NETWORK}`];

        const vcn = this.topologyCollection.findEntityByName(vcnName, VIRTUAL_CLOUD_NETWORK);

        if (!vcn) {
          return;
        }

        let subnet = this.topologyCollection.findEntityByName(subnetName, SUBNET, { vcnId: vcn.id });
        if (!subnet) {
          // check if subnet is in reverse vnc (in case of inbound link)
          const oppositeVirtualCloudNetworkPrefix = direction === 'source' ? 'dst' : 'src';
          const oppositeVcnName = link[`${oppositeVirtualCloudNetworkPrefix}-${VIRTUAL_CLOUD_NETWORK}`];

          const oppositeVcn = this.topologyCollection.findEntityByName(oppositeVcnName, VIRTUAL_CLOUD_NETWORK);

          if (!oppositeVcn) {
            return;
          }

          subnet = this.topologyCollection.findEntityByName(subnetName, SUBNET, { vcnId: oppositeVcn.id });

          if (!subnet) {
            return;
          }
        }

        const { vcnId } = subnet;

        // add fallback to vcn if not expanded, and replace display name with id
        resultedLink = {
          ...resultedLink,
          [direction]: {
            ...resultedLink[direction],
            vcnId,
            fallbackNodes: [{ type: VIRTUAL_CLOUD_NETWORK, value: vcnId }],
            value: subnet.id,
            displayName: subnet.displayName
          }
        };
      }

      if (link[direction].type.includes('src-') || link[direction].type.includes('dst-')) {
        resultedLink[direction].type = resultedLink[direction].type.replace('src-', '').replace('dst-', '');
      }

      // fall back peering to vcn
      if (link[direction].type === LOCAL_PEERING_GATEWAY) {
        const peeringName = link[direction].value;

        const peering = this.topologyCollection.findEntityByName(peeringName, LOCAL_PEERING_GATEWAY);
        if (!peering) {
          return;
        }

        const { vcnId } = peering;
        resultedLink[direction].fallbackNodes = [{ type: VIRTUAL_CLOUD_NETWORK, value: vcnId }];
      }

      if (link[direction].type === DYNAMIC_ROUTING_GATEWAY) {
        const drgName = link[direction].value;

        const drg = this.topologyCollection.findEntityByName(drgName, DYNAMIC_ROUTING_GATEWAY);
        if (!drg) {
          return;
        }

        // replace name with id
        resultedLink[direction].value = drg.id;
      }

      if (link[direction].type === IP_SEC_CONNECTION) {
        const ipSecConName = link[direction].value;

        const ipSecConnection = this.topologyCollection.findEntityByName(ipSecConName, IP_SEC_CONNECTION);
        if (!ipSecConnection) {
          return;
        }

        // replace name with id
        resultedLink[direction].value = ipSecConnection.id;
      }
    });

    const { source, target } = resultedLink;
    if (target.type === SUBNET && source.type === DYNAMIC_ROUTING_GATEWAY) {
      const vcnName = resultedLink[`dst-${VIRTUAL_CLOUD_NETWORK}`];

      const vcn = this.topologyCollection.findEntityByName(vcnName, VIRTUAL_CLOUD_NETWORK);

      if (!vcn) {
        return resultedLink;
      }

      let subnet =
        this.topologyCollection.findEntityById(target.value, SUBNET) ??
        this.topologyCollection.findEntityByName(target.value, SUBNET, { vcnId: vcn.id });

      if (!subnet) {
        const oppositeVcnName = resultedLink[`src-${VIRTUAL_CLOUD_NETWORK}`];
        const oppositeVcn = this.topologyCollection.findEntityByName(oppositeVcnName, VIRTUAL_CLOUD_NETWORK);

        if (!oppositeVcn) {
          return resultedLink;
        }

        subnet = this.topologyCollection.findEntityByName(target.value, SUBNET, { vcnId: oppositeVcn.id });

        if (!subnet) {
          return resultedLink;
        }
      }

      const { vcnId } = subnet;

      const drgAttachment = Object.values(this.topologyCollection.Entities[DYNAMIC_ROUTING_GATEWAY_ATTACHMENT]).find(
        (attachment) => attachment.vcnId === vcnId
      );

      if (!drgAttachment) {
        return resultedLink;
      }

      return [
        {
          ...resultedLink,
          source,
          target: {
            type: DYNAMIC_ROUTING_GATEWAY_ATTACHMENT,
            value: drgAttachment.id,
            vcnId,
            fallbackNodes: [{ type: VIRTUAL_CLOUD_NETWORK, value: vcnId }],
            displayName: drgAttachment.displayName
          }
        },
        {
          ...resultedLink,
          source: {
            type: DYNAMIC_ROUTING_GATEWAY_ATTACHMENT,
            value: drgAttachment.id,
            vcnId,
            fallbackNodes: [{ type: VIRTUAL_CLOUD_NETWORK, value: vcnId }],
            displayName: drgAttachment.displayName
          },
          target
        }
      ];
    }

    return resultedLink;
  }

  get nodeLinks() {
    if (!this.shouldRenderLinksBasedOnState) {
      return [];
    }

    const { nodeLinks, activeNode, hoveredNode } = this.state;

    return memoize(
      () =>
        rewriteFallbackLinks({ links: this.nodeLinksToDraw, mapDocument: this.map.current }).filter(
          ({ source, target }) => source.value !== target.value
        ),
      () => `${nodeLinks.length}-${activeNode?.value ?? ''}-${hoveredNode?.value ?? ''}`
    )();
  }

  get nodeLinksToDraw() {
    const statePathLinks = super.nodeLinks;

    const staticLinks = this.calculateStaticLinks();

    const allLinks = statePathLinks.concat(staticLinks);

    const { activeNode, hoveredNode } = this.state;
    if (hoveredNode || activeNode) {
      const links = generateLinksForHighlightedNode(allLinks, activeNode || hoveredNode);
      return links;
    }

    const totalStatePathLinks = statePathLinks.flatMap((statePathLink) => {
      const { source, target } = statePathLink;

      if (
        (['box', 'internet'].includes(source.type) && target.type === SUBNET) ||
        (source.type === SUBNET && ['box', 'internet'].includes(target.type))
      ) {
        const isSubnetSource = source.type === SUBNET;
        const subnetDirectionString = isSubnetSource ? 'source' : 'target';

        // example subnet -> IGV -> Internet
        const connectionGateways = [INTERNET_GATEWAY, NAT_GATEWAY, SERVICE_GATEWAY];
        const connectionGatewayType = connectionGateways.find((gatewayType) => statePathLink?.[gatewayType]);

        if (!connectionGatewayType) {
          return [];
        }

        const gatewayName = statePathLink?.[connectionGatewayType];
        const gatewayEntity = this.topologyCollection.findEntityByName(gatewayName, connectionGatewayType);

        // do not draw links to entities that not in topology. (or no enrichment)
        if (!gatewayEntity) {
          return [];
        }

        const subnetVcnId = statePathLink[subnetDirectionString].vcnId;

        return [
          {
            ...statePathLink,
            [subnetDirectionString]: {
              type: connectionGatewayType,
              value: gatewayEntity.id,
              fallbackNodes: [
                {
                  type: VIRTUAL_CLOUD_NETWORK,
                  value: subnetVcnId
                }
              ]
            }
          },
          {
            ...statePathLink,
            [isSubnetSource ? 'target' : 'source']: {
              type: connectionGatewayType,
              value: gatewayEntity.id,
              fallbackNodes: [
                {
                  type: VIRTUAL_CLOUD_NETWORK,
                  value: subnetVcnId
                }
              ]
            }
          }
        ];
      }

      return statePathLink;
    });

    const linksMap = new Map();

    // filter out duplicate links and sum their traffic
    totalStatePathLinks.forEach((link) => {
      const { source, target } = link;

      const key = [source, target]
        .map(({ type, value }) => `${type}-${value}`)
        .sort()
        .join('-');

      const hadOutboundData = Object.prototype.hasOwnProperty.call(link, 'outbound');

      link.inbound = link.inbound || 0;
      link.outbound = link.outbound || 0;
      link.total = link.total || 0;
      link.paths = link.paths || [];

      link.isArrowLink = true;

      // always draw when outbound is not defined
      link.isForwardPath = !hadOutboundData || link.outbound > 0;
      link.isReversePath = link.inbound > 0;

      if (linksMap.has(key)) {
        const existingLink = linksMap.get(key);

        if (existingLink.source.value === source.value) {
          existingLink.inbound += link.inbound;
          existingLink.outbound += link.outbound;
        } else {
          existingLink.inbound += link.outbound;
          existingLink.outbound += link.inbound;
        }

        if (link.source.value === existingLink.target.value) {
          existingLink.isReversePath = true;
        }

        existingLink.total += link.total;
        existingLink.paths = existingLink.paths.concat(link.paths);
      } else {
        linksMap.set(key, link);
      }
    });

    const resultedLinks = Array.from(linksMap.values());

    return resultedLinks
      .reverse() // reverse links to ensure path is drawn last
      .map(this.styleLink);
  }

  handleNodeLinkClick = (config) => {
    const { cloudProvider, isEmbedded, setSidebarDetails } = this.props;
    const { topology } = this.state;

    if (config.pathLink) {
      // if this is a 'show path to' link, use the subnet/destination cidr as source/target
      config.source = config.pathLink.source;
      config.target = config.pathLink.target;
    }

    if (!isEmbedded) {
      setSidebarDetails({
        type: 'link',
        nodeData: {},
        initialLinkQuery: config.query ?? null,
        source: config.source,
        target: config.target,
        links: this.nodeLinks,
        cloudProvider,
        topology
      });
    }
  };

  get topologyCollection() {
    const { $hybridMap } = this.props;
    return $hybridMap.ociCloudMapCollection;
  }

  /*
    @borrowed from CloudAwsMap.getVpcBoxPositions
  */
  getRegionMiniBoxPositions = memoize(
    ({ source }) => {
      const rect = this.map.current.querySelector('.region-mini-map')?.getBoundingClientRect();
      const left = (rect?.left ?? 0) - source.svg.rect.left;
      const right = (rect?.right ?? 0) - source.svg.rect.left;
      const top = (rect?.top ?? 0) - source.svg.rect.top;
      const bottom = (rect?.bottom ?? 0) - source.svg.rect.top;

      const Cx = ((rect?.right ?? 0) - (rect?.left ?? 0)) / 2;
      const Cy = ((rect?.Top ?? 0) - (rect?.Bottom ?? 0)) / 2;

      return { left, right, Cx, Cy, top, bottom };
    },
    // vnet box left and right position wont change
    () => this.map.current.querySelector('.region-mini-map')?.className
  );

  getNodeLinkPositions(data) {
    const { linkData, source, target, path, pathIndex } = data;
    const { points: sourcePoints } = source;
    const { points: targetPoints } = target;

    if (sourcePoints.length === 0 || targetPoints.length === 0) {
      return { source, target };
    }

    const { regionCx, regionLeft, regionRight, regionTop, regionBottom } = this.getRegionBoxPositions({ source });
    /* box for expanded VCN */
    const regionMiniBoxPositions = this.getRegionMiniBoxPositions({ source });

    const connector = CloudMapConnector({
      sourceNode: linkData.source,
      targetNode: linkData.target,
      path,
      source,
      target,
      regionCx,
      pathIndex,
      regionTop,
      regionLeft,
      regionRight,
      regionBottom,
      regionMiniBoxPositions,
      connectionType: linkData.connectionType
    });

    if (connector !== null) {
      const { sourceAnchor, targetAnchor, connectionPoints } = connector;
      this.setLinkAnchor(source, sourceAnchor);
      this.setLinkAnchor(target, targetAnchor);

      return super.getNodeLinkPositions({
        ...data,
        source: { ...source, points: sourcePoints.concat(connectionPoints) },
        target
      });
    }

    // eslint-disable-next-line no-console
    console.error('NO CONNECTOR FOUND FOR', source, target, linkData);

    return super.getNodeLinkPositions({
      ...data,
      source: { ...source, points: sourcePoints },
      target
    });
  }

  handleGridItemHoverToggle = ({ type, item }) => {
    if (type === 'enter') {
      this.setState({ hoveredNode: { type: item.type, value: item.id } });
    } else {
      this.setState({ hoveredNode: null });
    }
  };

  handleGridItemClick = ({ key, item }) => {
    const { topology } = this.topologyCollection;
    const { setSidebarDetails, $kaizen } = this.props;

    // Clear general topology context when user opens details for specific entities
    if ($kaizen) {
      this.clearGeneralTopologyContext();
      this.clearAllEntityFocusContexts();
    }

    // Add AI context for the selected entity
    if ($kaizen) {
      try {
        const contextName = `${key} ${item.displayName || item.id}`;
        const cleanNodeData = this.createCleanNodeData(item);

        const aiContext = {
          type: 'entity_focus',
          name: contextName,
          entity: key, // Use the entity type directly
          context: `User opened details for ${key} "${item.displayName || item.id}" on oci cloud map.`,
          model: {
            nodeType: key,
            nodeValue: item.displayName || item.id,
            cloudProvider: 'oci',
            entityType: key, // Add this for more explicit entity type mapping
            action: 'show_details',
            nodeData: cleanNodeData
          },
          suggestions: this.getEntitySpecificSuggestions(key)
        };

        $kaizen.addChatContext(aiContext);
      } catch (error) {
        console.warn('Failed to add AI context for grid item:', error);
      }
    }

    this.setState({ activeNode: { value: item.id, type: key ?? item.type } }, () => {
      setSidebarDetails({
        topology,
        type: key,
        nodeData: { ...item },
        cloudProvider: 'oci',
        value: item?.displayName ?? item.id
      });
    });
  };

  handleSelectNode({ type, value, nodeData = {}, force = false }) {
    const { $kaizen } = this.props;
    const { topology } = this.topologyCollection;

    // Ensure nodeData is populated from topology if not provided
    if (!nodeData || Object.keys(nodeData).length === 0) {
      nodeData = topology.Entities?.[type]?.[value] || {};
    }

    // Clear general topology context when user interacts with specific nodes
    if ($kaizen) {
      this.clearGeneralTopologyContext();

      // Add AI context for entity selection (this covers entities that bypass handleGridItemClick)
      try {
        const contextName = `${type} ${nodeData.displayName || nodeData.id || value}`;
        const cleanNodeData = this.createCleanNodeData(nodeData);

        // Map entity type to the correct constant for icon display
        const entityTypeToIcon = {
          [NAT_GATEWAY]: 'natGateways',
          [INTERNET_GATEWAY]: 'internetGateways',
          [SERVICE_GATEWAY]: 'serviceGateways',
          [VIRTUAL_CLOUD_NETWORK]: 'vcn',
          [SUBNET]: 'subnets'
        };
        const entityForIcon = entityTypeToIcon[type] || type;

        const aiContext = {
          type: 'entity_focus',
          name: contextName,
          entity: entityForIcon, // Use the mapped entity for icon
          context: `User selected ${type} "${nodeData.displayName || nodeData.id || value}" in OCI topology.`,
          model: {
            nodeType: type,
            nodeValue: nodeData.displayName || nodeData.id || value,
            cloudProvider: 'oci',
            entityType: type,
            action: 'select_node',
            nodeData: cleanNodeData
          },
          suggestions: this.getEntitySpecificSuggestions(type)
        };

        $kaizen.addChatContext(aiContext);
      } catch (error) {
        console.warn('Failed to add AI context for node selection:', error);
      }
    }

    super.handleSelectNode({ type, value, force, nodeData });
  }

  handleShowDetails = ({ type, value, nodeData }) => {
    const { topology } = this.topologyCollection;
    const { openPopover } = this.props;

    // First, add AI context for the entity selection
    this.handleSelectNode({ type, value, nodeData });

    const { node } = this.getNodePosition({ type, value: value ?? nodeData.id });
    const { x, y, width, height } = node;

    if (type === VIRTUAL_CLOUD_NETWORK || type === REGION) {
      return this.handleGridItemClick({ key: type, item: nodeData });
    }

    return openPopover({
      type,
      topology,
      value: value ?? nodeData.displayName,
      nodeData: { ...nodeData },
      cloudProvider: 'oci',
      position: { left: x, top: y, width, height },
      placement: 'bottom',
      detail: {
        type: 'cloud',
        cloudProvider: 'oci'
      },
      shortcutMenu: {
        selectedNode: nodeData ?? { type, value, nodeData },
        isShowingConnections: false,
        showConnectionsCallback: this.setNodeLinks
      }
    });
  };

  createCleanNodeData = (nodeData) => {
    if (!nodeData || typeof nodeData !== 'object') {
      return nodeData;
    }

    // Create a clean copy without circular references and only include essential data
    const cleanData = {};

    // Safe properties to include (avoiding complex objects that might have circular refs)
    const safeProperties = [
      'id',
      'Name',
      'name',
      'displayName',
      'State',
      'state',
      'Type',
      'type',
      'region',
      'compartmentId',
      'availabilityDomain',
      'faultDomain',
      'cidrBlock',
      'vcnId',
      'routeTableId',
      'securityListIds',
      'dhcpOptionsId',
      'subnetType',
      'virtualRouterIp',
      'virtualRouterMac',
      'timeCreated',
      'lifecycleState',
      'drgId',
      'bgpSessionState',
      'customerAsn',
      'oracleAsn',
      'customerBgpAsn',
      'oracleBgpAsn'
    ];

    // Use lodash pick to get the safe properties, then filter for primitive values
    const pickedData = pick(nodeData, safeProperties);
    Object.entries(pickedData).forEach(([prop, value]) => {
      if (
        value !== undefined &&
        (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean')
      ) {
        cleanData[prop] = value;
      }
    });

    return cleanData;
  };

  getEntitySpecificSuggestions = (type) => {
    switch (type) {
      case VIRTUAL_CLOUD_NETWORK:
        return [
          'Show me all subnets in this VCN',
          'What are the security lists for this VCN?',
          'Show me the route tables for this VCN',
          'Explain the architecture of this VCN'
        ];
      case SUBNET:
        return [
          'Show me the traffic patterns for this subnet',
          'What security lists are applied to this subnet?',
          'Show me the route table for this subnet',
          'Explain the purpose of this subnet'
        ];
      case REGION:
        return [
          'Show me all resources in this region',
          'What is the network topology for this region?',
          'Show me costs for this region',
          'Compare this region to other regions'
        ];
      case NAT_GATEWAY:
        return [
          'Show me outbound traffic through this NAT Gateway',
          'What subnets are using this NAT Gateway?',
          'Check the route tables pointing to this NAT Gateway',
          'Explain the NAT Gateway configuration'
        ];
      case INTERNET_GATEWAY:
        return [
          'Show me internet traffic through this gateway',
          'What VCN is this Internet Gateway attached to?',
          'Check the routing configuration for this Internet Gateway',
          'Explain the Internet Gateway setup'
        ];
      case DYNAMIC_ROUTING_GATEWAY:
        return [
          'Show me all attachments for this DRG',
          'What VCNs are connected to this DRG?',
          'Show me the route tables for this DRG',
          'Explain the DRG routing configuration'
        ];
      case IP_SEC_CONNECTION:
        return [
          'Show me traffic through this IPSec connection',
          'What is the tunnel status?',
          'Check the BGP session state',
          'Explain the IPSec connection configuration'
        ];
      case SERVICE_GATEWAY:
        return [
          'Show me traffic through this service gateway',
          'What Oracle services are accessible?',
          'Check the service gateway configuration',
          'Explain the purpose of this service gateway'
        ];
      case LOCAL_PEERING_GATEWAY:
        return [
          'Show me peering connections for this gateway',
          'What VCNs are peered through this gateway?',
          'Check the peering status',
          'Explain the local peering configuration'
        ];
      default:
        return [
          `Explain this ${type} in detail`,
          `What is the purpose of this ${type}?`,
          'Show me related resources',
          'Are there any configuration issues?'
        ];
    }
  };

  get onPremTopologyToRender() {
    const { topology } = this.topologyCollection;
    const { width: rectWidth } = this.svg.current.getBoundingClientRect();

    const items = [
      {
        key: IP_SEC_CONNECTION,
        group: Object.values(topology.Entities[IP_SEC_CONNECTION]),
        name: 'IPSec Connection',
        getSubtitle: (ipSec) => ipSec.displayName,
        icon: <CloudIcon cloudProvider="oci" entity={IP_SEC_CONNECTION} width={ICON_SIZE} height={ICON_SIZE} />
      },
      {
        key: VIRTUAL_CIRCUIT,
        group: Object.values(topology.Entities[VIRTUAL_CIRCUIT]),
        name: 'Virtual Circuit',
        getSubtitle: (vc) => vc.displayName,
        icon: <CloudIcon cloudProvider="oci" entity={VIRTUAL_CIRCUIT} width={ICON_SIZE} height={ICON_SIZE} />
      }
    ];

    const willFit = items.every(({ group }) => getTotalWidth({ itemCount: group.length, width: 200 }) < rectWidth);

    return items.map((item) => ({ ...item, willFit })).filter((tempGroup) => tempGroup.group.length > 0);
  }

  get onPremRoutersToRender() {
    const { topology } = this.topologyCollection;
    const { width: rectWidth } = this.svg.current.getBoundingClientRect();
    const items = [
      {
        key: ROUTER,
        group: Object.values(topology.Entities[ROUTER]),
        name: 'Router',
        getSubtitle: (entity) => entity?.name ?? entity?.device_name ?? entity?.id ?? 'Unknown Device',
        icon: <CloudIcon cloudProvider="oci" entity={ROUTER} width={ICON_SIZE} height={ICON_SIZE} />
      }
    ];
    const willFit = items.every(
      ({ group }) =>
        getTotalWidth({ itemCount: group.length, gap: ON_PREM_FLEX_GAP }) < rectWidth - INTERNET_WIDTH.toPoints()
    );

    return items.map((item) => ({ ...item, willFit })).filter((tempGroup) => tempGroup.group.length > 0);
  }

  handleToggleRegionItem = ({ type, value }) => {
    const { $kaizen } = this.props;
    const { topology } = this.topologyCollection;
    const { expandedRegionItems } = this.state;

    // Check if this VCN is currently expanded
    const isCurrentlyExpanded = expandedRegionItems.some((item) => item.type === type && item.value === value);

    // Handle AI context for VCN entities
    if ($kaizen && (type === 'vcn' || type === VIRTUAL_CLOUD_NETWORK)) {
      if (isCurrentlyExpanded) {
        // VCN is being collapsed - remove ALL OCI entity_focus contexts
        // (since closing a VCN means the user is done with that scope)
        if ($kaizen.activeChatContext && typeof $kaizen.activeChatContext.forEach === 'function') {
          const ociEntityContextsToRemove = [];
          $kaizen.activeChatContext.forEach((context) => {
            if (context.type === 'entity_focus' && context.model?.cloudProvider === 'oci') {
              ociEntityContextsToRemove.push(context);
            }
          });

          ociEntityContextsToRemove.forEach((context) => {
            $kaizen.removeChatContext(context);
          });
        }
      } else if (topology?.Entities?.[VIRTUAL_CLOUD_NETWORK]) {
        // VCN is being expanded - add context
        const vcnEntity = Object.values(topology.Entities[VIRTUAL_CLOUD_NETWORK]).find((vcn) => vcn.id === value);
        if (vcnEntity) {
          // Clear general topology context when user interacts with specific nodes
          this.clearGeneralTopologyContext();

          // Clear any existing VCN entity focus contexts before adding new one
          if ($kaizen.activeChatContext && typeof $kaizen.activeChatContext.forEach === 'function') {
            const vcnContextsToRemove = [];
            $kaizen.activeChatContext.forEach((context) => {
              if (
                context.type === 'entity_focus' &&
                context.entity === 'vcn' &&
                context.model?.cloudProvider === 'oci'
              ) {
                vcnContextsToRemove.push(context);
              }
            });

            vcnContextsToRemove.forEach((context) => {
              $kaizen.removeChatContext(context);
            });
          }

          // Get the VCN name - OCI VCNs use 'displayName' property, fallback to 'name' or 'id'
          const vcnName = vcnEntity.displayName || vcnEntity.name || value || 'Unknown VCN';

          // Add context for the specific VCN with OCI cloud provider logo
          $kaizen.addChatContext({
            type: 'entity_focus',
            name: `VCN ${vcnName}`,
            entity: 'vcn', // This should trigger the VCN logo
            description: `User selected VCN "${vcnName}" in OCI topology. This virtual cloud network contains subnets and network resources.`,
            model: {
              cloudProvider: 'oci',
              entityType: 'vcn',
              nodeType: 'vcn',
              nodeData: vcnEntity
            }
          });
        }
      }
    }

    this.setState(({ expandedRegionItems: currentExpandedItems }) => {
      // remove if expanded
      if (isCurrentlyExpanded) {
        return {
          expandedRegionItems: [...currentExpandedItems].filter((item) => item.type !== type || item.value !== value)
        };
      }

      return {
        expandedRegionItems: [...currentExpandedItems, { type, value }]
      };
    });
  };

  renderMap() {
    const { cloudProvider, width } = this.props;
    const { boxExpanded, loading, activeNode, nodeLinkQueries, nodeLinksLoading } = this.state;

    const { topology } = this.topologyCollection;

    if (loading) {
      return <Spinner mt={100} />;
    }

    if (this.topologyCollection.isEmpty()) {
      return <EmptyState mt={4} icon="cloud" title="No Topology Found" />;
    }

    return (
      <Box>
        {nodeLinksLoading && (
          <Flex alignItems="center" justifyContent="center" position="fixed" zIndex="999" top="300px" left="50%">
            <Spinner intent="primary" />
          </Flex>
        )}
        {/* ON PREM DEVICES */}
        <Flex justifyContent="space-between" gap={`${GRID_ROWS_GAP}px`}>
          <Box mr={1}>
            <CloudMapBox
              title="On Prem"
              className={classNames('box-onprem', {
                highlighted: this.isBoxHighlighted('onprem')
              })}
              minWidth="20vw"
              minHeight={150}
              flex="1 1 auto"
              flexWrap="wrap"
              onExpandToggle={(isExpanded) => this.setBoxExpanded('onprem', isExpanded)}
              isExpanded={boxExpanded.onprem}
            >
              <ItemGrid
                items={this.onPremRoutersToRender}
                itemStroke="primary"
                highlightedNodes={this.highlightedNodes}
                onHoverToggle={this.handleGridItemHoverToggle}
                onClick={this.handleGridItemClick}
                emptyState={<EmptyState icon="folder-close" description="No On Prem Resources" />}
                showTitle={false}
                selectedNode={activeNode}
              />
            </CloudMapBox>
          </Box>

          {/* INTERNET BOX */}
          <Box ml={1}>
            <CloudMapBox
              title="Internet"
              className={classNames('box-internet', 'link-bottomcenter', {
                highlighted: this.isBoxHighlighted('internet')
              })}
              width={INTERNET_WIDTH}
              height={233}
              onExpandToggle={(isExpanded) => this.setBoxExpanded('internet', isExpanded)}
              isExpanded={boxExpanded.internet}
            >
              <TopKeys
                classPrefix="internet"
                selected={this.getSelected('internet')}
                hovered={this.getHovered('internet')}
                highlighted={this.getHighlighted('internet')}
                onSelect={(value) => this.handleShowDetails({ type: 'internet', value })}
                onTabChange={this.handleInternetTabChange}
                cloud={cloudProvider}
              />
            </CloudMapBox>
          </Box>
        </Flex>

        {/* IPSec Connections */}
        <ItemGrid
          items={this.onPremTopologyToRender}
          itemWidth={200}
          itemStroke="primary"
          highlightedNodes={this.highlightedNodes}
          onHoverToggle={this.handleGridItemHoverToggle}
          onClick={this.handleGridItemClick}
          selectedNode={activeNode}
        />

        {/* REGION BOXES */}
        <Flex flexDirection="column" gap={`${GRID_ROWS_GAP}px`}>
          {topology.Hierarchy?.Regions?.map((region) => (
            <Box key={region.id} className={`box-region ${getMapClassname({ type: REGION, value: region.id })}`}>
              <CloudMapBox p={0} minWidth={200} minHeight={100} isExpanded>
                <OciRegion
                  region={region}
                  width={width - LOCATION_PADDING}
                  expandRegionItem={this.expandRegionItem}
                  onToggleRegionItem={this.handleToggleRegionItem}
                  onShowDetails={this.handleShowDetails}
                />
              </CloudMapBox>
            </Box>
          ))}
        </Flex>

        <TrafficLinkGenerator inputs={nodeLinkQueries} onLinksUpdate={this.handleNodeLinksUpdate} />
      </Box>
    );
  }
}
